import api from 'api';
import { BoxWrapper, Button, Modal } from 'components/Base';
import React, { useEffect, useState } from 'react';
import { Notify } from 'utils';
import { MetaType } from 'store/modules/Meta/types';
import { useDebouncedCallback } from 'use-debounce';
import Toggle from './Toggle';
import Checkbox from 'components/Checkbox/Checkbox';
import { connect } from 'react-redux';
import actions from 'store/actions';
import Store from 'store/store';
import EVLogo from 'assets/images/EV_Logo_2021.png';
import MorningstarLogo from 'assets/images/Morningstar_Logo.png';
import { linkColor, redBackground, redText } from 'utils/colors';
import { Chip, Tooltip } from '@material-ui/core';

const { selectors } = Store;

type CapacityForLossTogglesProps = {
  isFirmAdmin: boolean;
  setFirmConfig: Function;
  riskProfiler: string;
  questionnaireLength: number;
  riskGroup: number;
  selectedProfiler: string | null;
  setSelectedProfiler: Function;
  advisor: any;
};

const CapacityForLossToggles: React.FC<CapacityForLossTogglesProps> = ({
  isFirmAdmin,
  setFirmConfig,
  riskProfiler,
  questionnaireLength,
  riskGroup,
  selectedProfiler,
  setSelectedProfiler,
  advisor
}) => {
  const [toggles, setToggles] = useState([]);
  const [togglesConfig, setTogglesConfig] = useState([] as number[]);
  const [isModelOpen, setIsModelOpen] = useState(false);
  const isMorningstarRiskProfilerApproved =
    advisor?.organization?.is_morningstar_risk_profiler_approved;

  const contactMessage = (clickableLink = true) => {
    const email = 'support@fintegrate.co.uk';

    return (
      <>
        Should you wish to switch to the Morningstar risk profiler, please get
        in touch at{' '}
        {clickableLink ? (
          <a
            href={`mailto:${email}`}
            style={{
              textDecoration: 'none',
              color: linkColor
            }}
          >
            {email}
          </a>
        ) : (
          <>{email}</>
        )}
        .
      </>
    );
  };

  const toggleSetting = useDebouncedCallback((value: string) => {
    const savedToggles = togglesConfig;
    const indexOf = savedToggles.indexOf(Number(value));
    if (indexOf !== -1) {
      savedToggles.splice(indexOf, 1);
    } else {
      savedToggles.push(Number(value));
    }
    setTogglesConfig([...savedToggles]);
  });

  const saveToggles = () => {
    api
      .updateCapacityForLossConfig(togglesConfig)
      .then(response => {
        if (response.status === 200) {
          Notify.success(
            'Your capacity for loss settings were successfully updated!'
          );
        }
      })
      .catch(err => Notify.error(err));
  };

  useEffect(() => {
    Promise.all([
      api.getCapacityForLossToggles(),
      api.getCapacityForLossConfig()
    ])
      .then(responses => {
        setToggles(responses[0].data);
        setTogglesConfig(
          responses[1].data.sort(
            (a: MetaType, b: MetaType) => Number(a.value) - Number(b.value)
          )
        );
      })
      .catch(err => Notify.error(err));
  }, []);

  const checkboxOptions = [
    {
      label: 'EValue',
      defaultChecked: false,
      image: EVLogo
    },
    {
      label: 'Morningstar',
      defaultChecked: false,
      image: MorningstarLogo
    }
  ];

  const questionnaireOptions = [
    { label: '10 questions', defaultChecked: false, value: '10' },
    { label: '25 questions', defaultChecked: true, value: '25' }
  ];

  const EvalueOptions = [
    { label: '1-5 (13 risk questions)', defaultChecked: false, value: '5' },
    { label: '1-7 (15 risk questions)', defaultChecked: false, value: '7' },
    { label: '1-10 (18 risk questions)', defaultChecked: true, value: '10' }
  ];

  const [checkboxState, setCheckboxState] = useState(
    Object.fromEntries(checkboxOptions.map(option => [option.label, false]))
  );

  const [questionnaireState, setQuestionnaireState] = useState(
    Object.fromEntries(
      questionnaireOptions.map(option => [
        option.value,
        option.value === questionnaireLength.toString()
      ])
    )
  );

  const [riskGroupState, setRiskGroupState] = useState(
    Object.fromEntries(
      EvalueOptions.map(option => [
        option.value,
        option.value === riskGroup.toString()
      ])
    )
  );

  useEffect(() => {
    // Set the checkboxState based on riskProfiler
    if (riskProfiler) {
      setCheckboxState(() =>
        Object.fromEntries(
          checkboxOptions.map(option => [
            option.label,
            option.label === riskProfiler
          ])
        )
      );
    }
  }, [riskProfiler]);

  useEffect(() => {
    // Initialize questionnaireState based on questionnaireLength
    if (questionnaireLength) {
      setQuestionnaireState(() =>
        Object.fromEntries(
          questionnaireOptions.map(option => [
            option.value,
            Number(option.value) === questionnaireLength
          ])
        )
      );
    }
  }, [questionnaireLength]);

  useEffect(() => {
    // Initialize riskGroup based on questionnaireLength
    if (riskGroup) {
      setRiskGroupState(() =>
        Object.fromEntries(
          EvalueOptions.map(option => [
            option.value,
            Number(option.value) === riskGroup
          ])
        )
      );
    }
  }, [riskGroup]);

  useEffect(() => {
    const profiler = Object.keys(checkboxState).find(
      (key: string) => checkboxState[key]
    );
    setSelectedProfiler(profiler || null);
  }, [checkboxState]);

  const toggleCheckbox = (label: string) => {
    setCheckboxState(() =>
      Object.fromEntries(
        checkboxOptions.map(option => [option.label, option.label === label])
      )
    );
  };

  const toggleQuestionnaire = (value: string) => {
    setQuestionnaireState(
      Object.fromEntries(
        questionnaireOptions.map(option => [
          option.value,
          option.value === value
        ])
      )
    );
  };

  const toggleRiskGroup = (value: string) => {
    setRiskGroupState(
      Object.fromEntries(
        EvalueOptions.map(option => [option.value, option.value === value])
      )
    );
  };

  const saveRiskProfiler = () => {
    if (
      !isMorningstarRiskProfilerApproved &&
      selectedProfiler === 'Morningstar'
    ) {
      setIsModelOpen(true);
      return;
    }
    const selectedQuestionnaire = Object.keys(questionnaireState).find(
      key => questionnaireState[key]
    );

    const selectedRiskGroup = Object.keys(riskGroupState).find(
      key => riskGroupState[key]
    );

    if (!selectedProfiler || (!selectedQuestionnaire && !selectedRiskGroup)) {
      Notify.error(
        'Please select a Risk Profiler and a Questionnaire Length before saving.'
      );
      return;
    }

    api
      .setFirmConfig({
        risk_profiler: selectedProfiler,
        ...(selectedQuestionnaire != null && {
          questionnaire_length: Number(selectedQuestionnaire)
        }),
        ...(selectedRiskGroup != null && {
          risk_group: Number(selectedRiskGroup)
        })
      })
      .then(response => {
        if (response.status === 200) {
          setFirmConfig(response);
          Notify.success(
            selectedProfiler === 'Morningstar'
              ? `${selectedProfiler} with Questionnaire Length ${selectedQuestionnaire} was successfully updated!`
              : `${selectedProfiler} with Risk Group ${selectedRiskGroup} was successfully updated!`
          );
        } else {
          Notify.error('Failed to update Risk Profiler. Please try again.');
        }
      })
      .catch(() => {
        Notify.error('An error occurred while updating the Risk Profiler.');
      });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {advisor.organization.is_beta && (
        <BoxWrapper
          title="Risk Profiler"
          subTitle="Choose the risk profiling provider your firm uses - EV or Morningstar (previously known as FinaMetrica). This selection determines how client risk tolerances is assessed and reported. Select the range for the risk scale and the number of questions in the assessment. A wider scale provides more granular risk levels, while additional questions increase the depth of the assessment."
        >
          <div style={{ height: 'max-content', margin: '8px', padding: '8px' }}>
            <div
              style={{
                gap: '2rem'
              }}
            >
              <div>
                <div style={{ marginBottom: '0.5rem', fontWeight: 600 }}>
                  Risk Profiler
                </div>
                <div style={{ display: 'flex' }}>
                  {checkboxOptions.map(({ label, image }) => (
                    <div
                      key={label}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginLeft: '20px'
                      }}
                    >
                      <Checkbox
                        onClick={() => toggleCheckbox(label)}
                        isChecked={checkboxState[label]}
                        disabled={!isFirmAdmin}
                      />
                      <img
                        src={image}
                        alt={label}
                        style={{
                          maxWidth: '120px',
                          maxHeight: '40px',
                          marginRight: '8px',
                          marginTop: '10px'
                        }}
                      />
                      {label === 'Morningstar' &&
                        !isMorningstarRiskProfilerApproved && (
                          <Tooltip title={contactMessage(false)} arrow>
                            <Chip
                              style={{
                                backgroundColor: redBackground,
                                color: redText
                              }}
                              size="small"
                              label="Locked"
                            />
                          </Tooltip>
                        )}
                    </div>
                  ))}
                </div>
              </div>

              <div>
                {selectedProfiler === 'Morningstar' && (
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <div
                      style={{
                        marginBottom: '0.5rem',
                        fontWeight: 600,
                        marginTop: '2rem'
                      }}
                    >
                      Questionnaire Length
                    </div>
                    {questionnaireOptions.map(({ label, value }) => (
                      <Checkbox
                        key={label}
                        onClick={() => toggleQuestionnaire(value)}
                        label={label}
                        isChecked={questionnaireState[value]}
                        disabled={!isFirmAdmin}
                      />
                    ))}
                  </div>
                )}
                {selectedProfiler === 'EValue' && (
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <div
                      style={{
                        marginBottom: '0.5rem',
                        fontWeight: 600,
                        marginTop: '2rem'
                      }}
                    >
                      Risk Scale
                    </div>
                    {EvalueOptions.map(({ label, value }) => (
                      <Checkbox
                        key={label}
                        onClick={() => toggleRiskGroup(value)}
                        label={label}
                        isChecked={riskGroupState[value]}
                        disabled={!isFirmAdmin}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
            {isFirmAdmin && (
              <Button
                style={{ marginTop: '16px' }}
                variant="contained"
                color="secondary"
                size="medium"
                onClick={saveRiskProfiler}
              >
                Save Risk Profiler
              </Button>
            )}
          </div>
        </BoxWrapper>
      )}
      {selectedProfiler === 'EValue' && (
        <BoxWrapper title="Risk Analysis Toggles">
          <div
            style={{
              height: 'max-content',
              margin: '8px',
              padding: '8px'
            }}
          >
            {toggles.map((toggle: MetaType) => {
              return (
                <Toggle
                  key={toggle.value}
                  object={toggle}
                  isChecked={togglesConfig.includes(Number(toggle.value))}
                  onClick={toggleSetting}
                  isFirmAdmin={isFirmAdmin}
                />
              );
            })}
            {isFirmAdmin && (
              <div style={{ marginTop: '16px' }}>
                <Button size="medium" onClick={saveToggles}>
                  Save Toggles
                </Button>
              </div>
            )}
          </div>
        </BoxWrapper>
      )}
      <Modal isOpen={isModelOpen} closeModalFunc={() => setIsModelOpen(false)}>
        <BoxWrapper
          title={'Request Morningstar risk profiler'}
          closeModal={() => setIsModelOpen(false)}
        >
          <div
            style={{
              display: 'flex',
              minWidth: '25rem',
              maxWidth: '100%',
              justifyContent: 'space-between',
              padding: '1rem'
            }}
          >
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}
            >
              <div>{contactMessage()}</div>
            </div>
          </div>
        </BoxWrapper>
      </Modal>
    </div>
  );
};

export default connect(
  (state: any) => ({
    riskProfiler: selectors.firmSelectors.riskProfiler(state),
    questionnaireLength: selectors.firmSelectors.questionnaireLength(state), // Map this from Redux state
    riskGroup: selectors.firmSelectors.riskGroup(state),
    advisor: selectors.advisorSelectors.getAdvisor(state)
  }),
  {
    setFirmConfig: actions.firmActions.setFirmConfig
  }
)(CapacityForLossToggles);
